import { Loader } from '@fountain/fountain-ui-components';
import {
  Button,
  Grid,
  Input,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core';
import { ChevronLeft, ChevronRight } from '@material-ui/icons';
import React, { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { TestableTypography } from 'components/_shared/TestableTypography';
import globalMessages from 'shared/global/messages';

import { messages } from './messages';
import { useStyles } from './styles';

// must be ordered lowest to highest
export const ROWS_PER_PAGE = [10, 20, 50, 100];

export interface StyledPaginationProps {
  currentPage?: number;
  lastPage?: number;
  isLoading?: boolean;
  recordsPerPage?: number;
  containerClass?: string;
  fillContainer?: boolean;
  setCurrentPage: (page: number) => void;
  setPerPage?: (perPage: number) => void;
}

export const StyledPagination: FC<StyledPaginationProps> = ({
  currentPage = 1,
  lastPage = currentPage,
  isLoading = false,
  recordsPerPage = 20,
  containerClass,
  fillContainer = false,
  setCurrentPage,
  setPerPage,
}) => {
  const intl = useIntl();
  const classes = useStyles();

  if (isLoading) {
    return (
      <Grid className={classes.root} container justify="center">
        <Loader />
      </Grid>
    );
  }

  // TODO: break up into smaller components where possible
  return (
    <Grid className={containerClass ?? classes.root}>
      <Grid
        alignItems="center"
        className={classes.paginationContent}
        container
        direction="row"
        justify="center"
      >
        {setPerPage && (
          <Grid className={classes.paginationLeftContent} item>
            <Grid alignItems="center" container direction="row">
              <Grid item>
                <Typography variant="body2">
                  <FormattedMessage {...globalMessages.showPerPage} />
                </Typography>
              </Grid>
              <Grid item>
                <Select
                  classes={{ root: classes.rowsPerPageSelect }}
                  disableUnderline
                  inputProps={{
                    id: 'rows-per-page',
                    name: 'rowsPerPage',
                  }}
                  data-testid="rowsPerPage"
                  onChange={event => setPerPage(Number(event.target.value))}
                  type="number"
                  value={recordsPerPage}
                >
                  {ROWS_PER_PAGE.map(option => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            </Grid>
          </Grid>
        )}
        <Grid
          className={fillContainer ? '' : classes.pageSelectContainer}
          item
          xs={setPerPage ? 6 : 12}
        >
          <Grid
            alignItems="center"
            container
            direction="row"
            justify="space-between"
          >
            <Grid item>
              <Button
                aria-label={intl.formatMessage(messages.prev)}
                component="div"
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                <ChevronLeft />
                <Typography>
                  <FormattedMessage {...messages.prev} />
                </Typography>
              </Button>
            </Grid>
            <Grid item>
              <Grid alignItems="center" container>
                <Grid item>
                  <InputLabel htmlFor="paginationCurrentPage">
                    <Typography>
                      <FormattedMessage {...messages.page} />
                    </Typography>
                  </InputLabel>
                </Grid>
                <Grid item>
                  {/*
                    Uncontrolled input exists because input value must be different from 'currentPage'. If input value
                    changes with 'currentPage' then re-render occurs, which won't allow for page inputs > 9.
                  */}
                  <Input
                    classes={{
                      input: classes.pageSelectInput,
                      root: classes.pageSelectInputRoot,
                    }}
                    data-testid="paginationCurrentPage"
                    id="paginationCurrentPage"
                    defaultValue={currentPage}
                    disableUnderline
                    onBlur={event => setCurrentPage(Number(event.target.value))}
                    onKeyDown={event =>
                      event.key === 'Enter' &&
                      (event.target as HTMLElement).blur()
                    }
                  />
                </Grid>
                <Grid item>
                  <Typography>
                    <FormattedMessage {...messages.of} />
                  </Typography>
                </Grid>
                <Grid className={classes.lastPage} item>
                  <TestableTypography className={classes.lastPage}>
                    {lastPage}
                  </TestableTypography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Button
                aria-label={intl.formatMessage(globalMessages.next)}
                component="div"
                disabled={currentPage === lastPage}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                <Typography>
                  <FormattedMessage {...globalMessages.next} />
                </Typography>
                <ChevronRight />
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
